<template>
  <div ref="sidebar" class="container page-sidebar" :style="`top: ${stickyTop}px;`">
    <div ref="sidebarWrapper" class="page-sidebar__wrapper">
      <slot name="content" />
    </div>
    <div v-if="tableOfContent" class="page-sidebar__toc">
      <h3 class="page-sidebar__table-of-contents-header"> On This Page </h3>
      <div class="page-sidebar__table-of-contents">
        <div class="page-sidebar__table-of-contents__line" />
        <div class="row">
          <div class="col-lg">
            <span
              :class="{
                'page-sidebar__table-of-contents__indicator': true,
                active: tableOfContentsActiveId == pageStore.pageId
              }"
            />
            <span
              :class="{
                'page-sidebar__table-of-contents__label': true,
                active: tableOfContentsActiveId == pageStore.pageId
              }"
              @click="scrollToElementById(pageStore.pageId, headerHeight, true, true)"
            >
              {{ tableOfContentFirstItemLabel ?? 'Overview' }}
            </span>
          </div>
        </div>
        <div v-for="item of tableOfContents" :key="item?.system.id" class="row">
          <div class="col-lg">
            <span
              :class="{
                'page-sidebar__table-of-contents__indicator': true,
                active: tableOfContentsActiveId == item?.system.id
              }"
            />
            <span
              :class="{
                'page-sidebar__table-of-contents__label': true,
                active: tableOfContentsActiveId == item?.system.id
              }"
              @click="scrollToContent(item?.system.id)"
            >
              {{ item.elements.tableOfContentLinksMenuTitle.value }}
            </span>
          </div>
        </div>
      </div>
      <div class="divider" />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useCssVar, useEventListener } from '@vueuse/core'
import findLast from 'lodash/findLast'
import debounce from 'lodash/debounce'
import { contentTypes, type TableOfContentLinks } from '~/models'
import { useInsightModalStore } from '~/stores/modals/insight-modal'

const props = defineProps<{
  tableOfContent?: boolean
  tableOfContentFirstItemLabel?: string
  headerSubNavigation?: boolean
}>()

const pageStore = usePageStore()
const sidebar = ref<HTMLElement | null>(null)
const sidebarWrapper = ref<HTMLElement | null>(null)
const headerHeight = parseFloat(useCssVar('--height-header').value)
const { scrollToElementById } = useSmoothScroll()
const tableOfContents = ref<TableOfContentLinks[]>([])
const tableOfContentsActiveId = ref<string>('')
const tableOfContentsSource = pageStore.getTableOfContents()
const insightModalStore = useInsightModalStore()

const stickyTop = ref(0)
const updateStickyTop = () => {
  if (sidebarWrapper.value) {
    const headerHeight =
      document.getElementById('global-header')?.getBoundingClientRect()?.height ?? 0
    const sidebarWrapperPaddingTop =
      parseFloat(
        window
          .getComputedStyle(sidebarWrapper.value.parentElement as HTMLElement)
          .getPropertyValue('padding-top')
      ) || 0
    const sidebarWrapperHeight =
      sidebarWrapper.value.getBoundingClientRect().height + sidebarWrapperPaddingTop
    stickyTop.value = headerHeight - sidebarWrapperHeight
  }
}

const filterTableOfContent = () => {
  const page = document.getElementById(pageStore.pageId)
  tableOfContentsActiveId.value = page?.id ?? ''
  const tableOfContentsElements = tableOfContentsSource
    .filter((item) => item.elements.tableOfContentLinksMenuTitle.value.length > 0)
    .map((item) => document.getElementById(item?.system.id))
    .filter((item) => item != null)
  tableOfContents.value = tableOfContentsSource.filter((item) =>
    tableOfContentsElements.map((i) => i.id).includes(item?.system.id)
  )

  removeInsightsFromTableOfContents()

  function removeInsightsFromTableOfContents() {
    if (insightModalStore.items.length === 0) {
      tableOfContents.value = tableOfContents.value.filter(
        (t) => t.system.type !== contentTypes.insight_list_page.codename
      )
    }
  }
}

const reorderTableOfContents = () => {
  const tableOfContentsElements = tableOfContents.value
    .map((item) => document.getElementById(item?.system.id))
    .filter((item) => item != null)
  const itemsWithPositions = tableOfContentsElements
    .map((element) => {
      if (element) {
        return {
          item: tableOfContents.value.find((item) => item?.system.id === element.id),
          position: element.getBoundingClientRect().top
        }
      }
      return null
    })
    .filter((item) => item !== null) as { item: any; position: number }[]
  itemsWithPositions.sort((a, b) => a.position - b.position)
  tableOfContents.value = itemsWithPositions.map((item) => item.item)
  const lastElementTopOutOfViewport = findLast(tableOfContents.value, (e) => {
    const content = document
      .getElementById(e?.system.id)
      ?.querySelector('.container, .container-fluid')
    if (!content) return false
    const computedStyle = getComputedStyle(content)
    const paddingTop = computedStyle.getPropertyValue('padding-top')
    const offset = parseFloat(paddingTop) * 2
    return content.getBoundingClientRect().top - (offset + 220) < 0
  })
  tableOfContentsActiveId.value = lastElementTopOutOfViewport
    ? lastElementTopOutOfViewport?.system.id
    : pageStore.pageId
}

const scrollToContent = (id: string) => {
  const content = document.getElementById(id)?.querySelector('.container, .container-fluid')
  if (!content) return
  const computedStyle = getComputedStyle(content)
  const paddingTop = computedStyle.getPropertyValue('padding-top')
  const offset = parseFloat(paddingTop) * 2
  scrollToElementById(id, offset + 100)
}

const updateSidebar = () => {
  updateStickyTop()
  debounce(
    () => {
      filterTableOfContent()
      reorderTableOfContents()
    },
    200,
    { leading: true }
  )()
}

onMounted(() => {
  nextTick(() => {
    updateSidebar()
    useEventListener(window, 'scroll', updateSidebar)
  })
})
</script>
